import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Download, ChevronDown, ChevronUp, Mail, CheckCircle2, XCircle, Eye, Send, Loader2, BellRing } from "lucide-react";
import { log } from "../../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Flag = ({ ok }) => (ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-[var(--c-muted)]" />);

export default function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: s }] = await Promise.all([
        axios.get(`${API}/admin/leads`, { withCredentials: true }),
        axios.get(`${API}/admin/leads/stats`, { withCredentials: true }),
      ]);
      setLeads(data.leads || []);
      setStats(s);
    } catch (err) {
      log.warn("Failed to load leads", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [nudging, setNudging] = useState(false);

  const runNudges = async () => {
    setNudging(true);
    try {
      const { data } = await axios.post(`${API}/admin/leads/send-nudges`, {}, { withCredentials: true });
      toast.success(`Nudges: ${data.sent} sent · ${data.skipped} skipped · ${data.failed} failed (${data.due} due)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nudge run failed");
    }
    setNudging(false);
  };

  const previewNudge = async (stage) => {
    try {
      const { data } = await axios.post(`${API}/admin/leads/nudge-preview`, {}, { params: { stage }, withCredentials: true });
      toast.success(`${data.message} (code ${data.code}, $${data.amount} off)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/leads/export.csv`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `texas-llc-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${leads.length} lead${leads.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Export failed");
    }
    setExporting(false);
  };

  return (
    <div data-testid="leads-panel" className="mb-6 rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[3px_3px_0px_var(--c-shadow)]">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <button data-testid="leads-toggle" onClick={() => setExpanded((e) => !e)} className="flex items-center gap-3 text-left">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-[var(--c-border)] bg-[#C8102E] text-white">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-base font-bold text-[var(--c-text)]">
              Checklist Leads (<span data-testid="leads-count">{leads.length}</span>)
            </h3>
            <p className="text-xs text-[var(--c-muted)]">Free-checklist email captures &middot; pushed to GoHighLevel when configured</p>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-[var(--c-muted)]" /> : <ChevronDown className="h-4 w-4 text-[var(--c-muted)]" />}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="leads-nudge-preview-button"
            onClick={() => previewNudge(1)}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2 text-xs font-bold text-[var(--c-text)] shadow-[2px_2px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5"
          >
            <Eye className="h-3.5 w-3.5" /> Preview day 3
          </button>
          <button
            data-testid="leads-nudge2-preview-button"
            onClick={() => previewNudge(2)}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2 text-xs font-bold text-[var(--c-text)] shadow-[2px_2px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5"
          >
            <Eye className="h-3.5 w-3.5" /> Preview day 7
          </button>
          <button
            data-testid="leads-send-nudges-button"
            onClick={runNudges}
            disabled={nudging}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-4 py-2 text-xs font-bold text-white shadow-[2px_2px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {nudging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send due nudges
          </button>
          <button
            data-testid="leads-export-button"
            onClick={exportCsv}
            disabled={exporting || leads.length === 0}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2 text-xs font-bold text-[var(--c-text)] shadow-[2px_2px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t-2 border-[var(--c-border)]">
          {stats && (
            <div data-testid="leads-stats" className="grid grid-cols-2 gap-3 px-5 py-4 sm:grid-cols-4">
              {[
                { key: "total", label: "Total leads", value: stats.total_leads, sub: `${stats.unsubscribed} unsubscribed` },
                { key: "accounts", label: "Became free accounts", value: stats.accounts, sub: `${stats.account_rate}% of leads` },
                { key: "paid", label: "Became paid customers", value: stats.paid, sub: `${stats.paid_rate}% of leads` },
                { key: "nudges", label: "Nudges delivered", value: `${stats.nudge1_sent} / ${stats.nudge2_sent}`, sub: "day 3 / day 7" },
              ].map((s) => (
                <div key={s.key} data-testid={`leads-stat-${s.key}`} className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--c-muted)]">{s.label}</p>
                  <p className="font-heading text-2xl font-black text-[#1E3A8A]">{s.value}</p>
                  <p className="text-[11px] text-[var(--c-muted)]">{s.sub}</p>
                </div>
              ))}
            </div>
          )}
          {leads.length === 0 ? (
            <p data-testid="leads-empty" className="px-5 py-6 text-sm text-[var(--c-muted)]">No leads yet — they&apos;ll appear here as visitors request the free checklist.</p>
          ) : (
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-[var(--c-surface)] text-[11px] uppercase tracking-wide text-[var(--c-muted)]">
                  <tr>
                    <th className="px-5 py-2">Email</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Captured</th>
                    <th className="px-3 py-2" title="Checklist email sent"><Mail className="h-3.5 w-3.5" /></th>
                    <th className="px-3 py-2">GHL</th>
                    <th className="px-3 py-2" title="Nudges: day 3 · day 7"><BellRing className="h-3.5 w-3.5" /></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.email} data-testid="lead-row" className="border-t border-[var(--c-border-light)] text-[var(--c-text)]">
                      <td className="px-5 py-2 font-medium">{l.email}</td>
                      <td className="px-3 py-2 text-[var(--c-muted)]">{l.name || "—"}</td>
                      <td className="px-3 py-2"><span className="rounded-full border border-[var(--c-border-light)] px-2 py-0.5 text-[11px]">{l.source}</span></td>
                      <td className="px-3 py-2 text-xs text-[var(--c-muted)]">{new Date(l.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2"><Flag ok={l.emailed} /></td>
                      <td className="px-3 py-2"><Flag ok={l.ghl_pushed} /></td>
                      <td className="px-3 py-2 text-[11px] text-[var(--c-muted)]" data-testid="lead-nudge-status">
                        {l.unsubscribed ? "unsubscribed" : `${l.nudge_status || "pending"} · ${l.nudge2_status || "—"}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
